import React, { forwardRef } from 'react';
import { Typography, RadioGroup, FormControlLabel, Radio, Checkbox, TextField, Button } from '@material-ui/core';
import firebase from "firebase/app";
import "firebase/database";
import "firebase/storage";
import ShortUrlService from '../services/ShortUrlService';
import AuthenticatedUrlCache from '../services/AuthenticatedUrlCache';
import VideoRecorder from './VideoRecorder';

// Helper function to get display URL for images (handles both legacy and new)
const getDisplayUrl = async (imageUrl) => {
  if (!imageUrl) return '';
  
  let finalUrl;
  
  // If it's a short URL (legacy), convert to full URL
  if (imageUrl.startsWith('q-')) {
    finalUrl = await ShortUrlService.toFullUrl(imageUrl);
  } else {
    // Otherwise, return as-is (should be full Firebase Storage URL)
    finalUrl = imageUrl;
  }
  
  // Handle Firebase Storage URLs with authentication
  if (finalUrl.includes('firebasestorage.googleapis.com')) {
    // Always use authenticated access for secure storage
    try {
      const authenticatedUrl = await AuthenticatedUrlCache.getAuthenticatedUrl(finalUrl);
      return authenticatedUrl;
    } catch (error) {
      console.error('Failed to get authenticated URL:', error);
      // Return the original URL as fallback instead of throwing error
      return finalUrl;
    }
  }
  
  return finalUrl;
};

// Authenticated Image Component
const AuthenticatedImage = ({ imageUrl, alt, style }) => {
  const [src, setSrc] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    const loadImage = async () => {
      try {
        setLoading(true);
        setError(false);
        const authenticatedUrl = await getDisplayUrl(imageUrl);
        setSrc(authenticatedUrl);
      } catch (err) {
        console.error('Failed to load authenticated image:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    if (imageUrl) {
      loadImage();
    }
  }, [imageUrl]);

  if (loading) {
    return <div style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>Loading image...</div>;
  }

  if (error) {
    return (
      <div style={{ 
        ...style, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        background: '#ffebee', 
        color: '#d32f2f',
        fontSize: '12px',
        textAlign: 'center',
        padding: '8px'
      }}>
        🔒 Authentication required<br/>
        Please login to view this image
      </div>
    );
  }

  return (
    <img 
      src={src} 
      alt={alt} 
      style={style}
      onError={(e) => {
        console.error('Image failed to load:', src);
        e.target.style.display = 'none';
      }}
    />
  );
};

const QuestionRenderer = React.memo(forwardRef(({ question, answer, onAnswerChange, compact, examCode, userId, disabled = false }, ref) => {
  // Initialize ShortUrlService with Firebase config
  React.useEffect(() => {
    const firebaseConfig = firebase.app().options;
    ShortUrlService.autoInit(firebaseConfig);
  }, []);

  // Add authentication listener to ensure user is authenticated
  React.useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
      // User authentication state tracked silently
    });

    return () => unsubscribe();
  }, []);



  // Move hooks to the top to avoid conditional calls
  const [activeTab, setActiveTab] = React.useState('problem');
  

  const [selectedLang, setSelectedLang] = React.useState(() => {
    if (question.type === 'code' && answer && typeof answer === 'object' && answer.language) {
      return answer.language;
    }
    return 'Python';
  });
  // Store code per language
  const defaultBoilerplates = {
    Python: 'def solution():\n    pass\n',
    Java: 'public class Solution {\n    public static void main(String[] args) {\n        // your code here\n    }\n}',
    'C++': '#include <iostream>\nusing namespace std;\nint main() {\n    // your code here\n    return 0;\n}',
    JavaScript: 'function solution() {\n    // your code here\n}',
    Go: 'package main\nimport "fmt"\nfunc main() {\n    // your code here\n}',
    Ruby: 'def solution\n  # your code here\nend',
    'C#': 'using System;\nclass Solution {\n    static void Main(string[] args) {\n        // your code here\n    }\n}',
    PHP: '<?php\nfunction solution() {\n    // your code here\n}\n?>',
    Kotlin: 'fun main() {\n    // your code here\n}',
    Swift: 'func solution() {\n    // your code here\n}',
  };
  const [codeByLang, setCodeByLang] = React.useState({ ...defaultBoilerplates });



  // When language changes, update answer if user hasn't edited (only for code questions)
  React.useEffect(() => {
    if (question.type === 'code') {
      if (!codeByLang[selectedLang] && defaultBoilerplates[selectedLang]) {
        setCodeByLang(prev => ({ ...prev, [selectedLang]: defaultBoilerplates[selectedLang] }));
      }
      // If answer is not set, set it to the boilerplate for the selected language
      if ((answer === null || answer === undefined) && defaultBoilerplates[selectedLang]) {
        onAnswerChange(question.id, { code: defaultBoilerplates[selectedLang], language: selectedLang });
      }
    }
  }, [selectedLang, question.type]);

  // When user edits code
  const handleCodeChange = (e) => {
    const newCode = e.target.value;
    setCodeByLang(prev => ({ ...prev, [selectedLang]: newCode }));
    onAnswerChange(question.id, { code: newCode, language: selectedLang });
  };

  // Reset to boilerplate for current language
  const handleReset = () => {
    setCodeByLang(prev => ({ ...prev, [selectedLang]: defaultBoilerplates[selectedLang] }));
    onAnswerChange(question.id, { code: defaultBoilerplates[selectedLang], language: selectedLang });
  };







  const handleMcqSingleChange = (e) => {
    onAnswerChange(question.id, e.target.value);
  };

  const handleMcqMultiChange = (e) => {
    const currentAnswers = answer || [];
    const newAnswers = e.target.checked
      ? [...currentAnswers, e.target.name]
      : currentAnswers.filter(ans => ans !== e.target.name);
    onAnswerChange(question.id, newAnswers);
  };

  const handleTextChange = (e) => {
    onAnswerChange(question.id, e.target.value);
  };

  const handleFileChange = (e) => {
    onAnswerChange(question.id, e.target.files[0]);
  };

  const handleVideoUrlChange = (e) => {
    onAnswerChange(question.id, e.target.value);
  };

  const handleVideoUrlPaste = (e) => {
    // Allow paste for video questions
    e.stopPropagation();
  };

  switch (question.type) {
    case 'mcq-single':
      return (
        <div>
          <Typography variant="h6" style={compact ? { fontSize: 14 } : {}}>{question.text}</Typography>
          {question.imageUrl && (
            <div style={{ marginTop: 8, marginBottom: 12 }}>
              <AuthenticatedImage 
                imageUrl={question.imageUrl}
                alt="Question" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: 300, 
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }} 
              />
            </div>
          )}
          <RadioGroup value={answer || ''} onChange={handleMcqSingleChange}>
            {question.options.map(opt => (
              <FormControlLabel key={opt.id} value={opt.id} control={<Radio />} label={opt.text} style={compact ? { fontSize: 10 } : {}} />
            ))}
          </RadioGroup>
        </div>
      );
    case 'mcq-multi':
      return (
        <div>
          <Typography variant="h6" style={compact ? { fontSize: 14 } : {}}>{question.text}</Typography>
          {question.imageUrl && (
            <div style={{ marginTop: 8, marginBottom: 12 }}>
              <AuthenticatedImage 
                imageUrl={question.imageUrl}
                alt="Question" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: 300, 
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }} 
              />
            </div>
          )}
          {question.options.map(opt => (
            <FormControlLabel
              key={opt.id}
              control={
                <Checkbox
                  checked={(answer || []).includes(opt.id)}
                  onChange={handleMcqMultiChange}
                  name={opt.id}
                />
              }
              label={opt.text}
              style={compact ? { fontSize: 10 } : {}}
            />
          ))}
        </div>
      );
    case 'text':
      return (
        <div>
          <Typography variant="h6" style={compact ? { fontSize: 14 } : {}}>{question.text}</Typography>
          {question.imageUrl && (
            <div style={{ marginTop: 8, marginBottom: 12 }}>
              <AuthenticatedImage 
                imageUrl={question.imageUrl}
                alt="Question" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: 300, 
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }} 
              />
            </div>
          )}
          <TextField
            value={typeof answer === 'string' ? answer : ''}
            onChange={handleTextChange}
            variant="outlined"
            placeholder={`write your answer here`}
            fullWidth
            multiline
            minRows={4}
            InputProps={{ style: { fontSize: 18, lineHeight: 1.7 } }}
          />
        </div>
      );
    case 'code': {
      const showReset = codeByLang[selectedLang] && defaultBoilerplates[selectedLang] && codeByLang[selectedLang] !== defaultBoilerplates[selectedLang];
      const hasDiscussion = question.discussion || question.forumLink;
      return (
        <div style={{ marginTop: 16, padding: 0 }}>
          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: '1.5px solid #e0e0e0', marginBottom: 12 }}>
            <button
              type="button"
              onClick={() => setActiveTab('problem')}
              style={{
                background: 'none',
                border: 'none',
                borderBottom: activeTab === 'problem' ? '3px solid #1976d2' : '3px solid transparent',
                color: activeTab === 'problem' ? '#1976d2' : '#333',
                fontWeight: 700,
                fontSize: 15,
                padding: '8px 18px',
                cursor: 'pointer',
                outline: 'none',
                transition: 'border 0.2s',
              }}
            >Problem</button>

            {hasDiscussion && (
              <button
                type="button"
                onClick={() => setActiveTab('discussion')}
                style={{
                  background: 'none',
                  border: 'none',
                  borderBottom: activeTab === 'discussion' ? '3px solid #1976d2' : '3px solid transparent',
                  color: activeTab === 'discussion' ? '#1976d2' : '#333',
                  fontWeight: 700,
                  fontSize: 15,
                  padding: '8px 18px',
                  cursor: 'pointer',
                  outline: 'none',
                  transition: 'border 0.2s',
                }}
              >Discussion</button>
            )}
          </div>

          {/* Tab Content */}
          {activeTab === 'problem' && (
            <>
              {/* Problem Title */}
              {/* <div style={{ fontWeight: 700, color: '#1976d2', fontSize: 18, marginBottom: 8 }}>
                {question.text}
              </div> */}
              {/* Problem Description */}
              {question.problemDescription && (
                <div style={{
                  background: '#f5f7fa',
                  border: '1px solid #e0e0e0',
                  borderRadius: 6,
                  padding: 16,
                  marginBottom: 10,
                  maxHeight: 180,
                  overflowY: 'auto',
                  fontSize: 15,
                  color: '#222',
                  whiteSpace: 'pre-line',
                }}>
                  {question.problemDescription}
                </div>
              )}
              {/* Question Image */}
              {question.imageUrl && (
                <div style={{ marginBottom: 10 }}>
                  <img 
                    src={getDisplayUrl(question.imageUrl)} 
                    alt="Question" 
                    style={{ 
                      maxWidth: '100%', 
                      maxHeight: 300, 
                      border: '1px solid #ddd',
                      borderRadius: 8,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }} 
                    onError={(e) => {
                      console.error(`Failed to load image for question ${question.id}:`, question.imageUrl);
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
              {/* Instructions */}
              {question.instructions && (
                <div style={{ color: '#888', fontStyle: 'italic', fontSize: 14, marginBottom: 10 }}>
                  {question.instructions}
                </div>
              )}
            </>
          )}

          {activeTab === 'discussion' && hasDiscussion && (
            <div style={{ marginBottom: 12 }}>
              {question.discussion && (
                <div style={{ color: '#333', fontSize: 15, marginBottom: 8, whiteSpace: 'pre-line' }}>{question.discussion}</div>
              )}
              {question.forumLink && (
                <a href={question.forumLink} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', fontWeight: 600, fontSize: 15 }}>
                  Go to Forum Discussion
                </a>
              )}
            </div>
          )}

          {/* Language Selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, margin: '18px 0 8px 0' }}>
            <div style={{ fontWeight: 600, fontSize: 15, color: '#333' }}>Language:</div>
            <select
              value={selectedLang}
              onChange={e => {
                setSelectedLang(e.target.value);
                // If answer is object, update language
                if (question.type === 'code' && answer && typeof answer === 'object') {
                  onAnswerChange(question.id, { code: codeByLang[e.target.value] || defaultBoilerplates[e.target.value], language: e.target.value });
                }
              }}
              style={{
                fontSize: 15,
                padding: '4px 12px',
                borderRadius: 6,
                border: '1.5px solid #e0e0e0',
                background: '#fff',
                color: '#1976d2',
                fontWeight: 600,
                outline: 'none',
                minWidth: 120,
              }}
            >
              <option value="Python">Python</option>
              <option value="Java">Java</option>
              <option value="C++">C++</option>
              <option value="JavaScript">JavaScript</option>
              <option value="Go">Go</option>
              <option value="Ruby">Ruby</option>
              <option value="C#">C#</option>
              <option value="PHP">PHP</option>
              <option value="Kotlin">Kotlin</option>
              <option value="Swift">Swift</option>
            </select>
          </div>

          {/* Code Editor Label */}
          <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4, color: '#333' }}>Your Code</div>
          {/* Code Editor */}
          <TextField
            value={answer && typeof answer === 'object' && answer.language === selectedLang ? answer.code : codeByLang[selectedLang] !== undefined ? codeByLang[selectedLang] : defaultBoilerplates[selectedLang]}
            onChange={handleCodeChange}
            variant="outlined"
            fullWidth
            multiline
            minRows={12}
            placeholder={`// Write your code here (${selectedLang})`}
            InputProps={{
              style: {
                fontSize: 15,
                fontFamily: 'Fira Mono, Menlo, Monaco, Consolas, monospace',
                background: '#fcfcff',
                borderRadius: 6,
                border: '1.5px solid #e0e0e0',
                padding: 12,
                lineHeight: 1.6,
              }
            }}
            style={{ fontSize: 15, fontFamily: 'Fira Mono, Menlo, Monaco, Consolas, monospace', marginBottom: 8 }}
          />
          {/* Reset to Boilerplate Button */}
          {showReset && (
            <Button
              variant="outlined"
              size="small"
              style={{ marginTop: 4, fontSize: 13, fontWeight: 600, color: '#1976d2', borderColor: '#1976d2' }}
              onClick={handleReset}
            >
              Reset to Boilerplate
            </Button>
          )}


        </div>
      );
    }
    case 'upload':
      return (
        <div style={{ marginTop: 16 }}>
          <Typography variant="h6" gutterBottom style={compact ? { fontSize: 14 } : {}}>{question.instructions}</Typography>
          <input
            type="file"
            onChange={handleFileChange}
            style={{ display: 'none' }}
            id={`file-upload-${question.id}`}
          />
          <label htmlFor={`file-upload-${question.id}`}>
            <Button variant="contained" component="span" style={compact ? { fontSize: 12 } : {}}>
              Choose File
            </Button>
          </label>
          {answer && <Typography style={{ display: 'inline', marginLeft: 16, fontSize: 12 }}>{answer.name}</Typography>}
        </div>
      );
    case 'video':
      return (
        <VideoRecorder
          ref={ref}
          question={question}
          answer={answer}
          onAnswerChange={onAnswerChange}
          disabled={disabled}
          examCode={examCode}
          userId={userId}
          autoSaveOnSubmit={true}
        />
      );
    case 'psyco': {
      const error = !/^[ABCD]{4}$/.test(answer || '') || new Set((answer || '').toUpperCase()).size !== 4;
      return (
        <div>
          <Typography variant="body1" style={{ marginBottom: 8, fontWeight: 500 }}>{question.text}</Typography>
          {question.imageUrl && (
            <div style={{ marginTop: 8, marginBottom: 12 }}>
              <AuthenticatedImage 
                imageUrl={question.imageUrl}
                alt="Question" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: 300, 
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }} 
              />
            </div>
          )}
          {question.problemDescription && (
            <div style={{ 
              marginTop: 8, 
              marginBottom: 12, 
              padding: 12, 
              background: '#f5f5f5', 
              border: '1px solid #ddd',
              borderRadius: 8 
            }}>
              <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8, color: '#1976d2' }}>
                📋 Options and Scenarios:
              </Typography>
              <Typography variant="body2" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                {question.problemDescription}
              </Typography>
            </div>
          )}
          <TextField
            label="Enter your answer (A, B, C, D)"
            value={answer || ''}
            onChange={e => {
              const val = e.target.value.toUpperCase();
              if (val.length <= 4 && /^[ABCD]*$/.test(val)) onAnswerChange(question.id, val);
            }}
            error={!!answer && error}
            helperText={!!answer && error ? 'Answer must be a permutation of A, B, C, D' : ''}
            variant="outlined"
            fullWidth
            inputProps={{ maxLength: 4, style: { fontSize: 18, letterSpacing: 4, textTransform: 'uppercase' } }}
            style={{ marginTop: 8, marginBottom: 8 }}
          />
        </div>
      );
    }
    default:
      return <Typography style={compact ? { fontSize: 14 } : {}}>Unsupported question type.</Typography>;
  }
}));

QuestionRenderer.displayName = 'QuestionRenderer';

export default QuestionRenderer; 